import React, { useEffect, useMemo, useState } from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  createFilterOptions,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { applyFormQuestionOptionRangeWrite } from '../../api/formSampling';
import { relationFieldToRows } from '../../api/jenisPengukuranPerlengkapan';
import {
  strapiCreateDynamicEntity,
  strapiUpdateDynamicEntity,
  type DynamicEntityRow,
} from '../../api/strapiDynamicEntity';
import { useAuth } from '../../auth/AuthContext';
import { ChemInline } from '../../components/ChemInline';
import { queryKeys } from '../../query/queryKeys';
import { useMasterEntityMiniListQuery } from '../../query/hooks';

export const FORM_QUESTION_OPTION_ANSWER_TYPES = [
  'text',
  'number',
  'decimal',
  'boolean',
  'single',
  'multiple',
  'range',
] as const;

type OptionDraft = {
  documentId?: string;
  title: string;
  answerType: string;
  start: string;
  end: string;
  sortOrder: string;
};

function emptyOptionDraft(): OptionDraft {
  return {
    title: '',
    answerType: 'text',
    start: '',
    end: '',
    sortOrder: '0',
  };
}

function draftFromOptionRow(row: DynamicEntityRow): OptionDraft {
  return {
    documentId: row.documentId,
    title: String(row.title ?? ''),
    answerType: String(row.answer_type ?? 'text'),
    start: row.start == null || row.start === '' ? '' : String(row.start),
    end: row.end == null || row.end === '' ? '' : String(row.end),
    sortOrder: row.sort_order == null ? '0' : String(row.sort_order),
  };
}

export function optionLinkedToQuestion(row: DynamicEntityRow, questionDocumentId: string): boolean {
  const wanted = questionDocumentId.trim();
  if (!wanted) return false;
  return relationFieldToRows(row.form_question).some((rel) => String(rel.documentId ?? '').trim() === wanted);
}

export function sortOptionRows(rows: DynamicEntityRow[]): DynamicEntityRow[] {
  return [...rows].sort((a, b) => {
    const sa = Number(a.sort_order ?? 0);
    const sb = Number(b.sort_order ?? 0);
    if (sa !== sb) return sa - sb;
    return String(a.title ?? '').localeCompare(String(b.title ?? ''));
  });
}

export function findOptionRow(
  rows: DynamicEntityRow[],
  documentId: string | undefined,
): DynamicEntityRow | null {
  const id = documentId?.trim();
  if (!id) return null;
  return rows.find((row) => String(row.documentId ?? '').trim() === id) ?? null;
}

export type OptionChoice = DynamicEntityRow & { inputValue?: string };

const optionFilter = createFilterOptions<OptionChoice>();

export function optionChoiceLabel(option: OptionChoice | string): string {
  if (typeof option === 'string') return option;
  if (option.inputValue) return option.inputValue;
  return optionSummary(option);
}

export function draftFromOptionChoice(value: OptionChoice | string | null): OptionDraft {
  if (value == null) return emptyOptionDraft();
  if (typeof value === 'string') return { ...emptyOptionDraft(), title: value.trim() };
  if (value.inputValue) return { ...emptyOptionDraft(), title: value.inputValue.trim() };
  return draftFromOptionRow(value);
}

export function optionChoiceFromDraft(
  draft: OptionDraft,
  options: DynamicEntityRow[],
): OptionChoice | null {
  const existing = findOptionRow(options, draft.documentId);
  if (existing) return existing;
  const title = draft.title.trim();
  if (!title) return null;
  return { documentId: '', title, inputValue: title };
}

export function appendCreateOptionChoice(
  filtered: OptionChoice[],
  inputValue: string,
  existing: DynamicEntityRow[],
): OptionChoice[] {
  const input = inputValue.trim();
  if (!input) return filtered;
  const exists = existing.some((row) => String(row.title ?? '').trim().toLowerCase() === input.toLowerCase());
  if (exists) return filtered;
  return [...filtered, { documentId: '', title: input, inputValue: input }];
}

export function buildFormQuestionOptionWritePayload(
  draft: OptionDraft,
  questionDocumentId: string,
  alreadyLinked: boolean,
): Record<string, unknown> {
  const title = draft.title.trim();
  if (!title) throw new Error('Judul opsi wajib diisi.');
  const sortRaw = draft.sortOrder.trim();
  const sortOrder = sortRaw === '' ? 0 : Number(sortRaw);
  if (!Number.isFinite(sortOrder)) throw new Error('Urutan tidak valid.');
  const answerType = draft.answerType.trim();
  if (!FORM_QUESTION_OPTION_ANSWER_TYPES.includes(answerType as (typeof FORM_QUESTION_OPTION_ANSWER_TYPES)[number])) {
    throw new Error('Tipe jawaban tidak valid.');
  }
  const payload = applyFormQuestionOptionRangeWrite({
    title,
    answer_type: answerType,
    sort_order: sortOrder,
    start: draft.start.trim() === '' ? undefined : Number(draft.start),
    end: draft.end.trim() === '' ? undefined : Number(draft.end),
  });
  if (!alreadyLinked) {
    payload.form_question = { connect: [questionDocumentId.trim()].filter(Boolean) };
  }
  return payload;
}

export function buildFormQuestionOptionUnlinkPayload(questionDocumentId: string): Record<string, unknown> {
  const id = questionDocumentId.trim();
  if (!id) throw new Error('Pertanyaan tidak valid.');
  return { form_question: { disconnect: [id] } };
}

function optionSummary(row: DynamicEntityRow): string {
  const title = String(row.title ?? '').trim() || row.documentId;
  const type = String(row.answer_type ?? '').trim();
  if (type === 'range') {
    const start = row.start == null ? '' : String(row.start);
    const end = row.end == null ? '' : String(row.end);
    return `${title} (${type} ${start}–${end})`;
  }
  return type ? `${title} (${type})` : title;
}

type Props = {
  open: boolean;
  questionDocumentId: string;
  questionTitle: string;
  onClose: () => void;
};

export function FormQuestionRowOptionsDialog({ open, questionDocumentId, questionTitle, onClose }: Props) {
  const { jwt } = useAuth();
  const qc = useQueryClient();
  const optionsQ = useMasterEntityMiniListQuery(jwt, 'form-question-options', Boolean(jwt && open));
  const [draft, setDraft] = useState<OptionDraft>(emptyOptionDraft);
  const [error, setError] = useState<string | null>(null);

  const allOptions = useMemo(() => sortOptionRows(optionsQ.data ?? []), [optionsQ.data]);
  const linkedOptions = useMemo(
    () => allOptions.filter((row) => optionLinkedToQuestion(row, questionDocumentId)),
    [allOptions, questionDocumentId],
  );
  const selectedChoice = optionChoiceFromDraft(draft, allOptions);
  const alreadyLinked = selectedChoice && !selectedChoice.inputValue
    ? optionLinkedToQuestion(selectedChoice, questionDocumentId)
    : false;
  const isNewOption = Boolean(draft.title.trim()) && !draft.documentId;

  useEffect(() => {
    if (!open) return;
    setDraft(emptyOptionDraft());
    setError(null);
  }, [open, questionDocumentId]);

  async function invalidateOptionQueries() {
    await Promise.all([
      qc.invalidateQueries({ queryKey: queryKeys.masterEntityMini(jwt, 'form-question-options') }),
      qc.invalidateQueries({ queryKey: queryKeys.masterEntityMini(jwt, 'form-questions') }),
      qc.invalidateQueries({ queryKey: queryKeys.masterEntityListPrefix(jwt, 'form-question-options') }),
      qc.invalidateQueries({ queryKey: queryKeys.masterEntityListPrefix(jwt, 'form-questions') }),
    ]);
  }

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!jwt) throw new Error('Sesi tidak valid.');
      const payload = buildFormQuestionOptionWritePayload(draft, questionDocumentId, alreadyLinked);
      if (draft.documentId) {
        return strapiUpdateDynamicEntity(jwt, 'form-question-options', draft.documentId, payload, '*');
      }
      return strapiCreateDynamicEntity(jwt, 'form-question-options', payload, '*');
    },
    onSuccess: async () => {
      await invalidateOptionQueries();
      setDraft(emptyOptionDraft());
      setError(null);
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Gagal menyimpan opsi.'),
  });

  const unlinkMut = useMutation({
    mutationFn: async (row: DynamicEntityRow) => {
      if (!jwt) throw new Error('Sesi tidak valid.');
      const optionId = String(row.documentId ?? '').trim();
      if (!optionId) throw new Error('Opsi tidak valid.');
      return strapiUpdateDynamicEntity(
        jwt,
        'form-question-options',
        optionId,
        buildFormQuestionOptionUnlinkPayload(questionDocumentId),
        '*',
      );
    },
    onSuccess: async (_data, row) => {
      await invalidateOptionQueries();
      if (draft.documentId && draft.documentId === row.documentId) {
        setDraft(emptyOptionDraft());
      }
      setError(null);
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Gagal menghapus opsi.'),
  });

  const busy = saveMut.isPending || unlinkMut.isPending;

  const isRange = draft.answerType === 'range';

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (busy) return;
        onClose();
      }}
      fullWidth
      maxWidth="sm"
      data-testid="form-question-row-options-dialog"
    >
      <DialogTitle>Opsi pertanyaan — {questionTitle || 'Tanpa judul'}</DialogTitle>
      <Box
        component="form"
        onSubmit={(ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          setError(null);
          saveMut.mutate();
        }}
      >
        <DialogContent>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                Opsi terhubung
              </Typography>
              {optionsQ.isPending ? (
                <Typography variant="body2" color="text.secondary">
                  Memuat opsi…
                </Typography>
              ) : linkedOptions.length ? (
                <Stack spacing={0.5}>
                  {linkedOptions.map((row) => (
                    <Stack
                      key={row.documentId}
                      direction="row"
                      alignItems="center"
                      spacing={0.5}
                      sx={{ minWidth: 0 }}
                    >
                      <Link
                        component="button"
                        type="button"
                        underline="hover"
                        onClick={() => {
                          setDraft(draftFromOptionRow(row));
                          setError(null);
                        }}
                        data-testid={`form-question-row-option-${row.documentId}`}
                        sx={{ textAlign: 'left', minWidth: 0 }}
                      >
                        <ChemInline text={optionSummary(row)} />
                      </Link>
                      <Tooltip title="Hapus dari pertanyaan ini">
                        <span>
                          <IconButton
                            type="button"
                            size="small"
                            aria-label={`Hapus opsi ${String(row.title ?? row.documentId)}`}
                            disabled={busy}
                            onClick={() => unlinkMut.mutate(row)}
                            data-testid={`form-question-row-option-delete-${row.documentId}`}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Belum ada opsi untuk pertanyaan ini.
                </Typography>
              )}
            </Box>
            <Autocomplete
              freeSolo
              selectOnFocus
              clearOnBlur
              handleHomeEndKeys
              size="small"
              options={allOptions}
              value={selectedChoice}
              loading={optionsQ.isPending}
              onChange={(_, value) => {
                setDraft(draftFromOptionChoice(value));
                setError(null);
              }}
              filterOptions={(options, params) =>
                appendCreateOptionChoice(optionFilter(options, params), params.inputValue, allOptions)
              }
              getOptionLabel={optionChoiceLabel}
              isOptionEqualToValue={(a, b) => {
                if (a.inputValue || b.inputValue) return a.inputValue === b.inputValue;
                return a.documentId === b.documentId;
              }}
              renderOption={(props, row) => (
                <li {...props} key={row.inputValue ? `new:${row.inputValue}` : row.documentId}>
                  {row.inputValue ? (
                    `Buat opsi baru: ${row.inputValue}`
                  ) : (
                    <ChemInline text={optionSummary(row)} />
                  )}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Judul opsi"
                  required
                  helperText="Pilih opsi yang sudah ada, atau ketik judul baru."
                  inputProps={{
                    ...params.inputProps,
                    'data-testid': 'form-question-row-option-title',
                  }}
                />
              )}
            />
            <TextField
              select
              label="Tipe jawaban"
              size="small"
              fullWidth
              value={draft.answerType}
              onChange={(ev) => setDraft((p) => ({ ...p, answerType: ev.target.value }))}
              inputProps={{ 'data-testid': 'form-question-row-option-answer-type' }}
            >
              {FORM_QUESTION_OPTION_ANSWER_TYPES.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </TextField>
            {isRange ? (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  label="Start"
                  required
                  size="small"
                  type="number"
                  fullWidth
                  helperText="Inklusif. Contoh 1."
                  value={draft.start}
                  onChange={(ev) => setDraft((p) => ({ ...p, start: ev.target.value }))}
                  inputProps={{ 'data-testid': 'form-question-row-option-start' }}
                />
                <TextField
                  label="End"
                  required
                  size="small"
                  type="number"
                  fullWidth
                  helperText="Inklusif. Contoh 12 menghasilkan kolom 1–12."
                  value={draft.end}
                  onChange={(ev) => setDraft((p) => ({ ...p, end: ev.target.value }))}
                  inputProps={{ 'data-testid': 'form-question-row-option-end' }}
                />
              </Stack>
            ) : null}
            <TextField
              label="Urutan"
              size="small"
              type="number"
              value={draft.sortOrder}
              onChange={(ev) => setDraft((p) => ({ ...p, sortOrder: ev.target.value }))}
              inputProps={{ 'data-testid': 'form-question-row-option-sort' }}
            />
            {draft.documentId || draft.title.trim() ? (
              <Button
                type="button"
                size="small"
                sx={{ alignSelf: 'flex-start' }}
                onClick={() => {
                  setDraft(emptyOptionDraft());
                  setError(null);
                }}
              >
                Pilih opsi lain
              </Button>
            ) : null}
            {error ? (
              <Alert severity="error">{error}</Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button type="button" onClick={onClose} disabled={busy}>
            Tutup
          </Button>
          <Button type="submit" variant="contained" disabled={busy || !draft.title.trim()}>
            {saveMut.isPending
              ? 'Menyimpan…'
              : isNewOption
                ? 'Buat opsi'
                : alreadyLinked
                  ? 'Simpan opsi'
                  : 'Hubungkan opsi'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
